import { get, sum } from 'lodash';
import { RegionType, SocialFollowButtons as SocialNetwork, } from '@wix/shoutout-email-model-v3';
import { toNormalizedHueSatBr } from '../../utils/color';
import { adjustImage } from '../../utils/adjust-image';
import { defaultTextColor, defaultTextSize } from '../../utils/defaults';
import * as cssUtils from '../../utils/css-utils';
import { getContentWidth } from '../../utils/get-message-width';
import { getFooterColumnsWidths } from './footer-utils';
// Same icons are used for sharing over and linking social networks.
// They all should have 144x144 sizes.
const socialIcons = {
    [SocialNetwork.facebook]: '5e9922_549fe65f377b4459894488d785954edd~mv2.png',
    [SocialNetwork.pinterest]: '5e9922_11c1205feb0e4bad9e2936366a0117b1~mv2.png',
    [SocialNetwork.twitter]: '5e9922_64d32055fbe647738b345b72f05aef96~mv2.png',
    [SocialNetwork.instagram]: '5e9922_b329b26d01b040f3845afc82c7ed6f3d~mv2.png',
    [SocialNetwork.linkedIn]: '5e9922_931f3e857768439bb9293a3039d6f9ef~mv2.png',
    [SocialNetwork.soundCloud]: '5e9922_54172a17882547ba8d158d5bb05e0b57~mv2.png',
    [SocialNetwork.youtube]: '5e9922_205a9db5f0dd4185a5433e7183d1fe08~mv2.png',
    [SocialNetwork.tiktok]: '5e9922_8195d816da2c4eb59e43682d55a68471~mv2.png',
};
const sharingAlts = {
    [SocialNetwork.facebook]: 'COMPOSER.FOOTER.ALT.SHARE_VIA_FACEBOOK',
    [SocialNetwork.pinterest]: 'COMPOSER.FOOTER.ALT.SHARE_VIA_PINTEREST',
    [SocialNetwork.twitter]: 'COMPOSER.FOOTER.ALT.SHARE_VIA_TWITTER',
    [SocialNetwork.instagram]: '',
    [SocialNetwork.linkedIn]: '',
    [SocialNetwork.soundCloud]: '',
    [SocialNetwork.youtube]: '',
    [SocialNetwork.tiktok]: '',
};
const linkingAlts = {
    [SocialNetwork.facebook]: '',
    [SocialNetwork.pinterest]: '',
    [SocialNetwork.twitter]: '',
    [SocialNetwork.instagram]: '',
    [SocialNetwork.linkedIn]: '',
    [SocialNetwork.soundCloud]: '',
    [SocialNetwork.youtube]: '',
    [SocialNetwork.tiktok]: '',
};
const mySiteIconSrc = 'b49ee3_dd9b1a8812ae41138409a667954a6088~mv2.png';
const mySiteIconRtlSrc = 'a306cb_b5f719a9248c4930a356a660d73e14c1~mv2.png';
const mobilePlaceIconSrc = 'a306cb_c4f51e285cc7410f90c33964096d466c~mv2.png';
const isVisibleBusinessDetailsSection = ({ address, phone }) => (address.active && !!address.text.length) ||
    (phone.active && !!phone.text.length);
const isVisibleSocialIconsSection = (region) => {
    const { data } = region;
    return data.socialButtons?.active || data.socialLinks?.active || false;
};
const isVisibleTitleSection = ({ active, url }) => active && !!url?.length;
const isVisibleMobilePlacePromo = (mobilePlacePromo) => mobilePlacePromo?.active ?? false;
export const getFooterSection = ({ css, isRtl, lpProps, region, composerData, }) => {
    if (region.type !== RegionType.Footer) {
        throw Error('Footer must be passed');
    }
    const { businessDetails, socialButtons, socialLinks, siteTitle, mobilePlacePromo, } = region.data;
    const visibleBusinessDetails = isVisibleBusinessDetailsSection(businessDetails);
    const visibleSocialIcons = isVisibleSocialIconsSection(region);
    const visibleSiteTitle = isVisibleTitleSection(siteTitle);
    const visibleMobilePlacePromo = isVisibleMobilePlacePromo(mobilePlacePromo);
    if (!visibleBusinessDetails &&
        !visibleSocialIcons &&
        !visibleSiteTitle &&
        !visibleMobilePlacePromo) {
        return null;
    }
    const footerIconColor = cssUtils.prop('fill')([
        get(region, 'data.styles.icon'),
        get(css, 'so-global-footer-icon'),
        { fill: defaultTextColor },
    ]);
    const { br, hue, sat } = toNormalizedHueSatBr(footerIconColor);
    // NOTE: Make sure that ImageWithLoader from composer still works if you change this.
    const imageProps = {
        w: 750,
        h: 750,
        // FYI: adding params in different order might cause different result;
        br,
        sat,
        hue,
        lg: 0,
    };
    // NOTE: Make sure that ImageWithLoader from composer still works if you change this.
    const mobileImageProps = {
        w: 36,
        h: 36,
        // FYI: adding params in different order might cause different result;
        br,
        sat,
        hue,
        lg: 0,
    };
    const mobilePlaceIconUrlQuery = adjustImage(mobilePlaceIconSrc, mobileImageProps);
    const shareViaTitle = socialLinks?.active
        ? socialLinks.title
        : socialButtons.shareViaTitle;
    const buttonsOrLinks = socialLinks?.active
        ? socialLinks?.links
        : socialButtons.buttons;
    const buttons = buttonsOrLinks
        .filter(({ active }) => active)
        .filter(({ name }) => name in SocialNetwork)
        .map(({ name, url }) => ({
        src: adjustImage(socialIcons[name], imageProps),
        alt: socialLinks?.active ? linkingAlts[name] : sharingAlts[name],
        link: url,
        name,
    }));
    const goToSiteImageSrc = isRtl ? mySiteIconRtlSrc : mySiteIconSrc;
    const separatorColor = cssUtils.prop('borderColor')([
        get(region, 'data.styles.separator'),
        get(css, 'so-global-footer-separator'),
        { borderColor: defaultTextColor },
    ]);
    const rules = [css['so-global-region-footer'], css['so-global-region']];
    const mobileCssClass = cssUtils.getRegionMobileCssClass(rules);
    const innerPadding = cssUtils.padding(rules);
    const columnCount = sum([visibleBusinessDetails, visibleSocialIcons, visibleSiteTitle].map(Boolean));
    const contentWidth = getContentWidth(composerData, css, rules) -
        (innerPadding.left || 0) -
        (innerPadding.right || 0) -
        (columnCount - 1); // borders between columns
    const columnsProportions = Array.from({ length: columnCount }, () => 100 / columnCount);
    const columnsWidths = cssUtils.calculateColumnsProportions(contentWidth, columnsProportions, innerPadding, isRtl);
    const { businessColumnWidth, socialColumnWidth, titleWidth } = getFooterColumnsWidths(columnsWidths, visibleBusinessDetails, visibleSocialIcons, visibleSiteTitle);
    const isSocialButtonsFirst = !visibleBusinessDetails && visibleSocialIcons;
    const isSiteTitleFirst = !visibleBusinessDetails && !isSocialButtonsFirst && visibleSiteTitle;
    const isSocialButtonsLast = !visibleSiteTitle && visibleSocialIcons;
    const isBusinessDetailsLast = !visibleSiteTitle && !isSocialButtonsLast && visibleBusinessDetails;
    const rightPadding = `${innerPadding.right}px`;
    const leftPadding = `${innerPadding.left}px`;
    return {
        widthsForMediaQueries: [businessColumnWidth, socialColumnWidth, titleWidth],
        businessDetails: {
            address: getAddress(),
            phone: getPhone(),
            isVisible: visibleBusinessDetails,
            showBorderLeft: false,
            showBorderRight: false,
            width: businessColumnWidth,
            paddingLeft: businessPaddingLeft(),
            paddingRight: businessPaddingRight(),
        },
        socialButtons: {
            wrap: socialLinks?.active,
            buttons,
            isVisible: visibleSocialIcons,
            shareViaTitle,
            showBorderLeft: visibleBusinessDetails && separatorColor !== 'transparent',
            showBorderRight: visibleSiteTitle && separatorColor !== 'transparent',
            showCustomShareViaTitle: typeof shareViaTitle === 'string',
            width: socialColumnWidth,
            paddingLeft: socialPaddingLeft(),
            paddingRight: socialPaddingRight(),
        },
        siteTitle: {
            text: siteTitle.text,
            url: siteTitle.url,
            isVisible: visibleSiteTitle,
            showBorderLeft: visibleBusinessDetails &&
                !visibleSocialIcons &&
                separatorColor !== 'transparent',
            showBorderRight: false,
            hasTopDivider: !visibleSocialIcons,
            iconSrc: adjustImage(goToSiteImageSrc, imageProps),
            isRtl,
            width: titleWidth,
            paddingLeft: titlePaddingLeft(),
            paddingRight: titlePaddingRight(),
        },
        mobilePlacePromo: {
            ...getMobilePlacePromo(),
            isVisible: visibleMobilePlacePromo,
            iconSrc: mobilePlaceIconUrlQuery,
        },
        textColor: cssUtils.prop('color')([
            get(region, 'data.styles.text'),
            get(css, 'so-global-footer-text'),
            { color: defaultTextColor },
        ]),
        fontFamily: cssUtils.prop('fontFamily')([get(region, 'data.styles.text')]),
        separatorColor,
        fontSize: (get(css, 'so-global-footer-text.fontSize') ||
            defaultTextSize),
        showAd: lpProps.showAd,
        outerPadding: cssUtils.margin(rules),
        innerPadding,
        cssClass: mobileCssClass,
    };
    function businessPaddingLeft() {
        if (isRtl) {
            return isBusinessDetailsLast ? leftPadding : '0px';
        }
        else {
            return leftPadding;
        }
    }
    function businessPaddingRight() {
        if (isRtl) {
            return rightPadding;
        }
        else {
            return isBusinessDetailsLast ? rightPadding : '0px';
        }
    }
    function socialPaddingLeft() {
        if (isRtl) {
            return isSocialButtonsLast ? leftPadding : '0px';
        }
        else {
            return isSocialButtonsFirst ? leftPadding : '0px';
        }
    }
    function socialPaddingRight() {
        if (isRtl) {
            return isSocialButtonsFirst ? rightPadding : '0px';
        }
        else {
            return isSocialButtonsLast ? rightPadding : '0px';
        }
    }
    function titlePaddingLeft() {
        if (isRtl) {
            return leftPadding;
        }
        else {
            return isSiteTitleFirst ? leftPadding : '0px';
        }
    }
    function titlePaddingRight() {
        if (isRtl) {
            return isSiteTitleFirst ? rightPadding : '0px';
        }
        else {
            return rightPadding;
        }
    }
    function getAddress() {
        return {
            active: businessDetails.address.active,
            href: businessDetails.address.url,
            text: businessDetails.address.text,
        };
    }
    function getPhone() {
        return {
            active: businessDetails.phone.active,
            href: businessDetails.phone.url,
            text: businessDetails.phone.text,
        };
    }
    function getMobilePlacePromo() {
        return mobilePlacePromo
            ? {
                title: mobilePlacePromo.title,
                cta: mobilePlacePromo.cta,
                ctaUrl: mobilePlacePromo.ctaUrl,
            }
            : {};
    }
};
export const isFooterHidden = (composerData) => {
    const region = composerData.regions.find((r) => r.type === RegionType.Footer);
    if (!region) {
        return true;
    }
    const { businessDetails, siteTitle, mobilePlacePromo } = region.data;
    const visibleBusinessDetails = isVisibleBusinessDetailsSection(businessDetails);
    const visibleSocialIcons = isVisibleSocialIconsSection(region);
    const visibleSiteTitle = isVisibleTitleSection(siteTitle);
    const visibleMobilePlacePromo = isVisibleMobilePlacePromo(mobilePlacePromo);
    return (!visibleBusinessDetails &&
        !visibleSocialIcons &&
        !visibleSiteTitle &&
        !visibleMobilePlacePromo);
};
//# sourceMappingURL=get-footer-section.js.map